import groupBy from 'lodash.groupby';
import { DIBELS8, OLS, OLS_SPANISH, IDEL2, CLPL } from '../../constants/applications';
import OLSOfflineStorage from './OLSOfflineStorage';
import D8OfflineStorage from './D8OfflineStorage';
import Idel2OfflineStorage from './Idel2OfflineStorage';
import CLPLOfflineStorage from './CLPLOfflineStorage';
import { getSupportedApps, getGroupedData } from './storageUtils';
import { getAllAvailablePeriodsForApplication } from '../utils/getAvailableBenchmarkPeriods';

const APP_CLIENTS = {
    [OLS]: () => new OLSOfflineStorage(),
    [DIBELS8]: () => new D8OfflineStorage(),
    [IDEL2]: () => new Idel2OfflineStorage(),
    [CLPL]: () => new CLPLOfflineStorage(),
};

/**
 * Collect forms data from storage for the list of licensed apps.
 * App is skipped if it has no storage client in APP_CLIENTS or does not
 * support form data in SUPPORTED_APPS.
 * @param {Object} applications app object in format { <app name> : <app object> }
 * @param {Object} [dataClients] storage clients mapped by app name
 * @returns {Object} forms grouped by app and period:
 *                  { <app name> : { <period> : [<form 1>, ..., <form N>] } }
 */
export const getForms = async (applications, dataClients = APP_CLIENTS) => {
    const SUPPORTED_APPS = [DIBELS8, IDEL2];
    const appNames = getSupportedApps(applications, SUPPORTED_APPS);
    if (appNames.includes(IDEL2)) {
        appNames.push(CLPL); // we need to pull clpl forms as well that are stored in seperate repository
    }
    if (appNames.length === 0) {
        return Promise.resolve({});
    }
    const formsPromises = appNames.map(async appName => {
        const client = dataClients[appName]();
        const forms = await client.getForms();
        return { [appName]: groupBy(forms, 'period') };
    }, []);
    const forms = await Promise.all(formsPromises);
    return forms.reduce((formsMap, appForms) => ({ ...formsMap, ...appForms }), {});
};

/**
 * Collect cutscores data from storage for the list of licensed apps.
 * App is skipped if it has no storage client in APP_CLIENTS or does not
 * support cutscores data in SUPPORTED_APPS.
 * @param {Object} applications app object in format { <app name> : <app object> }
 * @returns {Object} cutscores grouped by app name, grade, period and measure:
 *                  { <app name> : { <grade> : { <period> : { <measure> : [<cutscore 1>, ..., <cutscore N>] } } } }
 */
export const getCutscores = async applications => {
    const SUPPORTED_APPS = [DIBELS8, IDEL2];
    const appNames = getSupportedApps(applications, SUPPORTED_APPS);
    if (appNames.includes(IDEL2)) {
        appNames.push(CLPL); // we need to pull clpl cutscores as well that are stored in sepereate repository
    }
    if (appNames.length === 0) {
        return Promise.resolve({});
    }
    const cutscoresPromises = appNames.map(async appName => {
        const client = APP_CLIENTS[appName]();
        const cutscores = await client.getCutscores();
        return { [appName]: getGroupedData(cutscores, ['grade', 'period', 'measure']) };
    }, []);
    const cutscores = await Promise.all(cutscoresPromises);
    return cutscores.reduce(
        (cutscoresMap, appCutscores) => ({ ...cutscoresMap, ...appCutscores }),
        {},
    );
};

/**
 * Collect goals from storage for the list of licensed apps.
 * App is skipped if it has no storage client in APP_CLIENTS or does not
 * support form data in SUPPORTED_APPS.
 * @param {Object} applications app object in format { <app name> : <app object> }
 * @param {Object} [dataClients] storage clients mapped by app name
 * @returns {Object} forms grouped by app
 *                  { <app name> : { [<goal 1>, ..., <goal N>] } }
 */
export const getGoals = async (applications, dataClients = APP_CLIENTS) => {
    const SUPPORTED_APPS = [DIBELS8, IDEL2];
    const appNames = getSupportedApps(applications, SUPPORTED_APPS);
    if (appNames.length === 0) {
        return Promise.resolve({});
    }
    const goalsPromises = appNames.map(async appName => {
        const client = dataClients[appName]();
        const goals = await client.getGoals();
        return {
            [appName]: getGroupedData(goals, ['studentUid', 'gradeLevel', 'period', 'measure']),
        };
    });
    const goals = await Promise.all(goalsPromises);
    return goals.reduce((goalsMap, appGoals) => ({ ...goalsMap, ...appGoals }), {});
};

/**
 * Collect growth zones from storage for the list of licensed apps.
 * App is skipped if it has no storage client in APP_CLIENTS or does not
 * support form data in SUPPORTED_APPS.
 * @param {Object} applications app object in format { <app name> : <app object> }
 * @param {Object} [dataClients] storage clients mapped by app name
 * @returns {Object} growth zones grouped by app:
 *                  { <app name> : { [<growth zone 1>, ..., <growth zone N>] } }
 */
export const getZoG = async (applications, dataClients = APP_CLIENTS) => {
    const SUPPORTED_APPS = [DIBELS8, IDEL2];
    const appNames = getSupportedApps(applications, SUPPORTED_APPS);
    if (appNames.length === 0) {
        return Promise.resolve({});
    }
    const ZoGPromises = appNames.map(async appName => {
        const client = dataClients[appName]();
        const ZoG = await client.getZoG();
        return { [appName]: groupBy(ZoG, 'studentUid') };
    });
    const ZoG = await Promise.all(ZoGPromises);
    return ZoG.reduce((ZoGMap, appZoG) => ({ ...ZoGMap, ...appZoG }), {});
};

/**
 * Collect and group results from storage for the list of licensed app
 * and students.
 * App is skipped if it has no storage client in APP_CLIENTS or does not
 * support results data in SUPPORTED_APPS.
 * @param {Object} applications app object in format { <app name> : <app object> }
 * @param {Array} students list of student objects
 * @param {Object} fourthPeriodAvailability periods availability by application group
 * @param {Object} [dataClients] storage clients mapped by app name
 * @returns {Object} grouped results for supported apps:
 *                  { <app name> : { <period> : { <student id> : [<result 1>, ..., <result N>] } } }
 */
export const getResults = async (
    applications,
    students,
    fourthPeriodAvailability,
    dataClients = APP_CLIENTS,
) => {
    const SUPPORTED_APPS = [OLS, DIBELS8, IDEL2];
    const appNames = Object.keys(applications).reduce((apps, appName) => {
        if (SUPPORTED_APPS.includes(appName) && !apps.includes(appName)) {
            return [...apps, appName];
        }
        // use ols app name for ols spa license to have all results under ols key
        if (appName === OLS_SPANISH && !apps.includes(OLS)) {
            return [...apps, OLS];
        }
        return apps;
    }, []);
    if (appNames.length === 0) {
        return Promise.resolve({});
    }
    const studentIds = students.map(student => student.id);
    const resultsPromises = appNames.map(async appName => {
        const client = dataClients[appName]();
        const results = await client.getStudentsResults(studentIds);
        const allPeriods = getAllAvailablePeriodsForApplication(appName, fourthPeriodAvailability);
        return {
            [appName]: allPeriods.reduce(
                (periodsMap, period) => ({
                    ...periodsMap,
                    [period]: groupBy(
                        results.filter(result => result.period === period),
                        'studentUid',
                    ),
                }),
                {},
            ),
        };
    }, []);
    const results = await Promise.all(resultsPromises);
    const resultsFromStore = results.reduce(
        (resultsMap, appResults) => ({ ...resultsMap, ...appResults }),
        {},
    );

    return resultsFromStore;
};
